import React from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "../components/navbar";
import Footer from "../components/footer";
import { mediaUrl, useSiteData } from "../lib/siteApi";
import solarWide from "../assets/images/solar/bg1.webp";
import solarRoof from "../assets/images/solar/17.jpg";
import solarPanels from "../assets/images/solar/19.jpg";
import building from "../assets/images/solar/bg3.webp";
import award from "../assets/images/solar/award.webp";
import charging from "../assets/images/solar/bg5.webp";
import yard from "../assets/images/solar/conta.webp";
import plant from "../assets/images/solar/kah.webp";
import { groupServices, prettyLabel, serviceStory } from "../lib/catalog";

function Back({ to }) {
  return (
    <Link to={to} className="inline-flex items-center gap-2 text-sm font-semibold text-teal-200 hover:text-white">
      ← Back
    </Link>
  );
}

function ServiceFrame({ backTo, kicker, title, image, children, related = [], quote = true }) {
  return (
    <>
      <Navbar />
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-teal-950 to-emerald-900 pb-16 pt-28 text-white sm:pb-20 sm:pt-32">
        <div className="absolute inset-0 opacity-25" style={{ backgroundImage: 'url("https://www.transparenttextures.com/patterns/cubes.png")' }} />
        <div className="container relative z-10">
          <Back to={backTo} />
          {kicker ? <p className="renerg-kicker mt-6 text-teal-300">{kicker}</p> : null}
          <h1 className="renerg-section mt-3 max-w-4xl text-white">{title}</h1>
        </div>
      </section>
      {image ? (
        <div className="relative z-10 -mt-8 sm:-mt-12">
          <img src={mediaUrl(image)} alt="" className="h-56 w-full object-cover sm:h-[380px]" />
        </div>
      ) : null}
      <main className="bg-slate-50 pb-16">
        <div className="service-stack">
          {children}
          {related.length > 0 && (
            <section className="border-t border-slate-200 bg-slate-50">
              <div className="container py-14">
                <p className="renerg-kicker text-teal-700">More in this work</p>
                <h2 className="renerg-h3 mt-3 max-w-3xl text-slate-900">Related services</h2>
                <p className="renerg-body mt-4 max-w-3xl text-slate-600">Other services in the same group. Open one to see what you get and how that work runs.</p>
                <div className={`mt-8 grid gap-4 ${related.length > 2 ? "sm:grid-cols-2 lg:grid-cols-3" : "md:grid-cols-2"}`}>
                  {related.map((item) => (
                    <Link key={item.to} to={item.to} className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 transition hover:border-teal-600">
                      {item.image ? (
                        <img src={mediaUrl(item.image)} alt="" className="mb-5 h-40 w-full rounded-xl object-cover" />
                      ) : (
                        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-teal-50 text-xl">{item.icon || "☀"}</span>
                      )}
                      <p className="renerg-kicker mt-5 text-teal-700">{item.kicker}</p>
                      <h3 className="renerg-h4 mt-2 text-slate-900">{item.title}</h3>
                      {item.summary ? <p className="renerg-small mt-2 flex-1 text-slate-600">{item.summary}</p> : null}
                      <span className="mt-4 font-semibold text-teal-700">Read more →</span>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
          {quote ? (
            <div className="service-band">
              <div className="container flex flex-col gap-3 py-8 sm:flex-row sm:items-center sm:justify-between">
                <p className="font-semibold text-slate-900">Ready to request this work?</p>
                <Link to="/contact" className="inline-flex justify-center rounded-xl bg-teal-700 px-5 py-3 font-semibold text-white">Get a quote</Link>
              </div>
            </div>
          ) : null}
        </div>
      </main>
      <Footer />
    </>
  );
}

function plain(value) {
  return String(value || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

function ServiceCards({ services, icon }) {
  return (
    <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {services.map((item) => (
        <Link key={item.id} to={`/services/${item.id}`} className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 transition hover:border-teal-600">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-teal-50 text-xl">{item.icon || icon || "☀"}</span>
          <h3 className="renerg-h4 mt-5 text-slate-900">{item.title}</h3>
          <p className="renerg-small mt-2 text-slate-600">{plain(item.description)}</p>
        </Link>
      ))}
    </div>
  );
}

export default function ServicesIndex() {
  const site = useSiteData();
  const grouped = groupServices(site.services || []);
  const cats = Object.values(grouped);

  return (
    <ServiceFrame backTo="/" kicker="What we do" title="Energy, buildings and mobility" quote={false}>
      {cats.map((cat) => {
        const sample = serviceStory(cat.services[0] || { category: cat.key });
        return (
          <section key={cat.key} className="border-t border-slate-200 bg-slate-50">
            <div className="container py-14">
              <p className="renerg-kicker text-teal-700">{sample.indexKicker}</p>
              <h2 className="renerg-h3 mt-3 max-w-3xl text-slate-900">{sample.indexTitle}</h2>
              <p className="renerg-body mt-4 max-w-3xl text-slate-600">{sample.indexLead}</p>
              <ServiceCards services={cat.services} icon={cat.icon} />
            </div>
          </section>
        );
      })}
    </ServiceFrame>
  );
}

export function ServiceCategoryPage() {
  const { category } = useParams();
  const site = useSiteData();
  const grouped = groupServices(site.services || []);
  const cat = grouped[category] || null;

  return (
    <ServiceFrame backTo="/services" kicker="Services" title={cat ? cat.title : prettyLabel(category)}>
      {!cat ? (
        <p className="text-slate-500">This service category was not found.</p>
      ) : (
        <section className="bg-slate-50">
          <div className="container py-14">
            <p className="renerg-kicker text-teal-700">{serviceStory(cat.services[0] || { category: cat.key }).indexKicker}</p>
            <h2 className="renerg-h3 mt-3 max-w-3xl text-slate-900">{cat.title}</h2>
            <p className="renerg-body mt-4 max-w-3xl text-slate-600">{cat.description}</p>
            <ServiceCards services={cat.services} icon={cat.icon} />
          </div>
        </section>
      )}
    </ServiceFrame>
  );
}

const STOCK = {
  photovoltaic: [solarWide, solarRoof, solarPanels],
  realestate: [building, award, solarRoof],
  emobility: [charging, yard, plant],
};

function boxPhotos(item) {
  if (Array.isArray(item.photos)) return item.photos.map((url) => mediaUrl(url)).filter(Boolean);
  const stock = STOCK[String(item.category || "").toLowerCase()] || STOCK.photovoltaic;
  const main = item.imageUrl ? mediaUrl(item.imageUrl) : "";
  return [main, ...stock].filter((src, index, list) => src && list.indexOf(src) === index).slice(0, 3);
}

function ServiceArrange({ item }) {
  const story = serviceStory(item);
  const photos = boxPhotos(item);
  return (
    <div>
      <section className="bg-slate-50">
        <div className="container grid items-start gap-10 py-14 lg:grid-cols-2">
          <div>
            <p className="renerg-kicker text-teal-700">What you get</p>
            <h2 className="renerg-h3 mt-3 text-slate-900">{story.getTitle}</h2>
            <p className="renerg-body mt-4 text-slate-600">{story.getLead}</p>
            <ul className="mt-8 space-y-5">
              {story.get.map(([title, text]) => (
                <li key={title} className="flex gap-3">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-teal-700 text-xs text-white">✓</span>
                  <div>
                    <p className="font-semibold text-slate-900">{title}</p>
                    <p className="renerg-small mt-1 text-slate-600">{text}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          {photos.length > 0 && (
            <div className="grid grid-cols-2 gap-4">
              {photos.map((src, index) => (
                <img key={`${src}-${index}`} src={src} alt="" className={index === 0 ? "col-span-2 h-64 w-full rounded-2xl object-cover" : "h-48 w-full rounded-2xl object-cover"} />
              ))}
            </div>
          )}
        </div>
      </section>
      <section className="bg-gradient-to-br from-slate-950 via-teal-950 to-emerald-900 text-white">
        <div className="container py-14">
          <p className="renerg-kicker text-teal-300">How the work runs</p>
          <h2 className="renerg-h3 mt-3 max-w-2xl text-white">{story.stepsTitle}</h2>
          <div className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {story.steps.map(([title, text], index) => (
              <div key={title} className="border-t border-white/20 pt-4">
                <p className="text-2xl font-semibold text-teal-200">{String(index + 1).padStart(2, "0")}</p>
                <h3 className="mt-3 font-semibold text-white">{title}</h3>
                <p className="renerg-small mt-2 text-teal-50/90">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="bg-white">
        <div className="container grid items-start gap-10 py-14 lg:grid-cols-2">
          <p className="renerg-h3 border-l-4 border-teal-600 pl-5 text-slate-900">{story.who}</p>
          <div>
            <p className="renerg-kicker text-teal-700">Who it is for</p>
            <h2 className="renerg-h3 mt-3 text-slate-900">{story.whoTitle}</h2>
            <p className="renerg-body mt-4 text-slate-600">{story.places}</p>
            <Link to="/contact" className="mt-8 inline-flex bg-teal-700 px-5 py-3 font-semibold text-white">Get started →</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export function ServiceDetailPage() {
  const { id } = useParams();
  const site = useSiteData();
  const item = (site.services || []).find((s) => s.id === id) || null;
  const grouped = groupServices(site.services || []);
  const cat = item ? grouped[String(item.category || "").toLowerCase()] : null;
  const related = (cat?.services || []).filter((s) => s.id !== id).slice(0, 2).map((s) => ({
    to: `/services/${s.id}`,
    title: s.title,
    image: s.imageUrl,
    icon: s.icon || cat?.icon,
    kicker: cat?.title,
    summary: String(s.description || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim(),
  }));

  return (
    <ServiceFrame
      backTo={cat ? `/services/category/${cat.key}` : "/services"}
      kicker={cat?.title || "Service"}
      title={item?.title || "Service"}
      related={related}
      quote={false}
    >
      {!item ? (
        <p className="container py-10 text-slate-500">Service not found.</p>
      ) : (
        <ServiceArrange item={item} />
      )}
    </ServiceFrame>
  );
}
